(function(){
/* Zumm Health — homepage */
const {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useMotionValueEvent,
  useSpring
} = window.Motion;
const {
  useState,
  useRef
} = React;
const {
  PRODUCTS,
  ORBIT_ORDER,
  Nav,
  Footer,
  TopBar,
  Microbes,
  Marquee,
  Reveal,
  fadeUp,
  stagger,
  useToast,
  buyClick
} = window.Z;

/* ============ ORBIT (solar system, viewed edge-on) ============ */
function OrbitItem({
  product,
  index,
  rotation,
  showToast
}) {
  // Each product sits 90° apart on a horizontal ring. rotation is a MotionValue (degrees).
  const angle = useTransform(rotation, r => (index * 90 - r) * Math.PI / 180);
  // Half the ring width/height (matches .orbit-ring ellipse)
  const x = useTransform(angle, a => Math.sin(a) * (Math.min(window.innerWidth * .47, 520) * 0.88));
  const depth = useTransform(angle, a => Math.cos(a)); // 1 = front, -1 = far back
  const yEllipse = useTransform(angle, a => Math.cos(a) * Math.min(window.innerHeight * .12, 108));
  const scale = useTransform(depth, d => { const t = (d + 1) / 2; return 0.34 + Math.pow(t, 1.7) * 1.10; });
  const opacity = useTransform(depth, d => 0.22 + (d + 1) / 2 * 0.78);
  const zIndex = useTransform(depth, d => Math.round((d + 1) * 50));
  const blur = useTransform(depth, d => `blur(${Math.max(0, (1 - d) * 3)}px) brightness(${(0.82 + (d + 1) / 2 * 0.26).toFixed(3)})`);
  return /*#__PURE__*/React.createElement(motion.a, {
    className: "orbit-item",
    href: product.page,
    "aria-label": product.name,
    style: {
      x,
      y: yEllipse,
      scale,
      opacity,
      zIndex,
      filter: blur,
      translateX: '-50%',
      translateY: '-50%'
    }
  }, /*#__PURE__*/React.createElement(motion.img, {
    src: product.img,
    alt: product.name,
    animate: {
      y: [0, -10, 0]
    },
    transition: {
      duration: 4 + index,
      repeat: Infinity,
      ease: 'easeInOut'
    }
  }));
}
function OrbitSection({
  showToast
}) {
  const ref = useRef(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end end']
  });
  // rotate the ring 270° total => each of the 4 products takes a turn at the front
  const rotationRaw = useTransform(scrollYProgress, [0.06, 0.94], [0, 270], {
    clamp: true
  });
  const rotation = useSpring(rotationRaw, {
    stiffness: 60,
    damping: 18,
    mass: .6
  });
  const [active, setActive] = useState(0);
  useMotionValueEvent(rotationRaw, 'change', r => {
    const idx = (Math.round(r / 90) % 4 + 4) % 4;
    setActive(idx);
  });
  const activeProduct = PRODUCTS[ORBIT_ORDER[active]];
  return /*#__PURE__*/React.createElement("section", {
    className: "orbit-section",
    id: "shop",
    ref: ref
  }, /*#__PURE__*/React.createElement("div", {
    className: "orbit-sticky"
  }, /*#__PURE__*/React.createElement(Microbes, {
    count: 10
  }), /*#__PURE__*/React.createElement("div", {
    className: "orbit-head wrap"
  }, /*#__PURE__*/React.createElement("span", {
    className: "eyebrow"
  }, "The Zumm System"), /*#__PURE__*/React.createElement("h2", {
    className: "h-lg"
  }, "Four planets.", /*#__PURE__*/React.createElement("br", null), "One healthy ", /*#__PURE__*/React.createElement("span", {
    className: "accent-green"
  }, "gut galaxy."))), /*#__PURE__*/React.createElement("div", {
    className: "orbit-stage"
  }, /*#__PURE__*/React.createElement("div", {
    className: "orbit-ring"
  }), /*#__PURE__*/React.createElement("div", {
    className: "orbit-ring inner"
  }), /*#__PURE__*/React.createElement(motion.div, {
    className: "orbit-sun",
    "aria-hidden": "true",
    animate: {
      scale: [1, 1.07, 1]
    },
    transition: {
      duration: 3.4,
      repeat: Infinity,
      ease: 'easeInOut'
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: window.ZUMM_IMG["favicon-192.png"],
    alt: ""
  })), ORBIT_ORDER.map((id, i) => /*#__PURE__*/React.createElement(OrbitItem, {
    key: id,
    product: PRODUCTS[id],
    index: i,
    rotation: rotation,
    showToast: showToast
  })), /*#__PURE__*/React.createElement("div", {
    className: "orbit-progress",
    "aria-hidden": "true"
  }, ORBIT_ORDER.map((id, i) => /*#__PURE__*/React.createElement("span", {
    key: id,
    className: 'orbit-dot' + (i === active ? ' on' : '')
  }))), /*#__PURE__*/React.createElement("div", {
    className: "scroll-hint"
  }, /*#__PURE__*/React.createElement(motion.span, {
    animate: {
      y: [0, 7, 0]
    },
    transition: {
      duration: 1.6,
      repeat: Infinity
    }
  }, "↓"), "Keep scrolling to orbit"), /*#__PURE__*/React.createElement("div", {
    className: "orbit-info"
  }, /*#__PURE__*/React.createElement(AnimatePresence, {
    mode: "wait"
  }, /*#__PURE__*/React.createElement(motion.div, {
    className: "orbit-card",
    key: activeProduct.id,
    initial: {
      opacity: 0,
      y: 26,
      scale: .96
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1
    },
    exit: {
      opacity: 0,
      y: -18,
      scale: .97
    },
    transition: {
      duration: .32,
      ease: 'easeOut'
    }
  }, /*#__PURE__*/React.createElement("h3", {
    style: {
      color: activeProduct.accent
    }
  }, activeProduct.name), /*#__PURE__*/React.createElement("p", {
    className: "sub"
  }, activeProduct.line), /*#__PURE__*/React.createElement("div", {
    className: "row"
  }, /*#__PURE__*/React.createElement("span", {
    className: "price-tag"
  }, "$", activeProduct.price.toFixed(2)), /*#__PURE__*/React.createElement(motion.a, {
    className: 'btn ' + activeProduct.btn,
    href: activeProduct.page,
    whileHover: {
      scale: 1.06
    },
    whileTap: {
      scale: .95
    }
  }, "Explore →"), /*#__PURE__*/React.createElement(motion.button, {
    className: "btn btn-ghost",
    onClick: buyClick(showToast, activeProduct),
    whileHover: {
      scale: 1.06
    },
    whileTap: {
      scale: .95
    }
  }, "Add to Cart"))))))));
}

/* ============ hero (compact, text-only — leads into the universe) ============ */
function Hero() {
  return /*#__PURE__*/React.createElement("header", {
    className: "hero hero-compact"
  }, /*#__PURE__*/React.createElement(Microbes, {
    count: 16
  }), /*#__PURE__*/React.createElement("div", {
    className: "wrap"
  }, /*#__PURE__*/React.createElement(motion.div, {
    className: "hero-center",
    initial: "hidden",
    animate: "visible",
    variants: stagger
  }, /*#__PURE__*/React.createElement(motion.span, {
    className: "eyebrow",
    variants: fadeUp
  }, "Pediatrician‑Formulated · GMP‑Certified"), /*#__PURE__*/React.createElement(motion.h1, {
    className: "h-xl",
    variants: fadeUp,
    style: {
      margin: '20px 0 18px'
    }
  }, "No gummies. No sugar.", /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("span", {
    className: "accent-orange"
  }, "No nonsense.")), /*#__PURE__*/React.createElement(motion.p, {
    className: "lead",
    variants: fadeUp,
    style: {
      margin: '0 auto'
    }
  }, "Most kids' vitamins are candy in disguise. Zumm Health makes clean, delayed‑release probiotics and sugar‑free vitamins that support your child's gut — formulated by a pediatric gut doctor, tested by third‑party labs."), /*#__PURE__*/React.createElement(motion.div, {
    className: "hero-ctas",
    variants: fadeUp,
    style: {
      justifyContent: 'center'
    }
  }, /*#__PURE__*/React.createElement(motion.a, {
    className: "btn btn-orange",
    href: "#shop",
    whileHover: {
      scale: 1.05
    },
    whileTap: {
      scale: .95
    }
  }, "Explore the System ↓"), /*#__PURE__*/React.createElement(motion.a, {
    className: "btn btn-ghost",
    href: "science.html",
    whileHover: {
      scale: 1.05
    },
    whileTap: {
      scale: .95
    }
  }, "See the Science")), /*#__PURE__*/React.createElement(motion.div, {
    className: "hero-badges",
    variants: fadeUp,
    style: {
      justifyContent: 'center'
    }
  }, ['🧪 Third‑Party Tested', '🚫 Sugar‑Free', '🌱 Non‑GMO', '💊 Delayed‑Release Capsules'].map(b => /*#__PURE__*/React.createElement("span", {
    className: "badge-pill",
    key: b
  }, b))))));
}

/* ============ why / tiles ============ */
function Why() {
  const tiles = [{
    ico: '🍬',
    bg: 'rgba(228,69,58,.18)',
    title: 'Gummies are candy in disguise',
    text: 'Most gummy vitamins carry 2–3 g of sugar per serving and lose potency fast. Ours carry zero sugar — the label proves it.'
  }, {
    ico: '🛡️',
    bg: 'rgba(94,139,196,.18)',
    title: 'Built to survive stomach acid',
    text: 'Delayed‑release capsules are designed to protect probiotics from stomach acid, so more of them reach the gut where they belong.'
  }, {
    ico: '🥼',
    bg: 'rgba(45,155,111,.18)',
    title: 'Formulated by a gut doctor',
    text: 'Every formula is designed by a board‑certified pediatric gastroenterologist and made in a GMP‑certified facility with third‑party testing.'
  }];
  return /*#__PURE__*/React.createElement("section", {
    className: "section",
    id: "why"
  }, /*#__PURE__*/React.createElement("div", {
    className: "wrap"
  }, /*#__PURE__*/React.createElement(Reveal, null, /*#__PURE__*/React.createElement("span", {
    className: "eyebrow orange"
  }, "Why Zumm")), /*#__PURE__*/React.createElement(Reveal, null, /*#__PURE__*/React.createElement("h2", {
    className: "h-lg",
    style: {
      margin: '18px 0 46px',
      maxWidth: 720
    }
  }, "Read any gummy label. ", /*#__PURE__*/React.createElement("span", {
    className: "accent-yellow"
  }, "Then read ours."))), /*#__PURE__*/React.createElement(Reveal, {
    className: "tiles",
    variants: stagger,
    amount: .25
  }, tiles.map(t => /*#__PURE__*/React.createElement(motion.div, {
    className: "tile",
    key: t.title,
    variants: fadeUp,
    whileHover: {
      y: -8
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "ico",
    style: {
      background: t.bg
    }
  }, t.ico), /*#__PURE__*/React.createElement("h3", null, t.title), /*#__PURE__*/React.createElement("p", null, t.text))))));
}

/* ============ product grid ============ */
function ProductGrid({
  showToast
}) {
  return /*#__PURE__*/React.createElement("section", {
    className: "section",
    style: {
      paddingTop: 40
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "wrap"
  }, /*#__PURE__*/React.createElement(Reveal, null, /*#__PURE__*/React.createElement("span", {
    className: "eyebrow green"
  }, "The Line‑Up")), /*#__PURE__*/React.createElement(Reveal, null, /*#__PURE__*/React.createElement("h2", {
    className: "h-lg",
    style: {
      margin: '18px 0 46px'
    }
  }, "Pick your ", /*#__PURE__*/React.createElement("span", {
    className: "accent-green"
  }, "planet"))), /*#__PURE__*/React.createElement(Reveal, {
    className: "grid-4",
    variants: stagger,
    amount: .2
  }, ORBIT_ORDER.map(id => {
    const p = PRODUCTS[id];
    return /*#__PURE__*/React.createElement(motion.div, {
      className: "pcard",
      key: id,
      variants: fadeUp,
      whileHover: {
        y: -10,
        borderColor: p.accent
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "glow",
      style: {
        background: p.glow
      }
    }), /*#__PURE__*/React.createElement("span", {
      className: "eyebrow",
      style: {
        fontSize: 10.5,
        padding: '5px 10px',
        borderColor: p.accent,
        color: p.accent,
        background: 'transparent'
      }
    }, p.tag), /*#__PURE__*/React.createElement("a", {
      href: p.page
    }, /*#__PURE__*/React.createElement(motion.img, {
      src: p.img,
      alt: p.name,
      whileHover: {
        scale: 1.06,
        rotate: 1.5
      },
      transition: {
        type: 'spring',
        stiffness: 260,
        damping: 18
      }
    })), /*#__PURE__*/React.createElement("h3", null, p.name), /*#__PURE__*/React.createElement("p", {
      className: "sub"
    }, p.line), /*#__PURE__*/React.createElement("span", {
      className: "price-tag"
    }, "$", p.price.toFixed(2)), /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'flex',
        gap: 10,
        marginTop: 10
      }
    }, /*#__PURE__*/React.createElement(motion.a, {
      className: 'btn ' + p.btn,
      style: {
        padding: '11px 20px',
        fontSize: 14
      },
      href: p.page,
      whileHover: {
        scale: 1.05
      },
      whileTap: {
        scale: .95
      }
    }, "Details"), /*#__PURE__*/React.createElement(motion.button, {
      className: "btn btn-ghost",
      style: {
        padding: '11px 16px',
        fontSize: 14
      },
      onClick: buyClick(showToast, p),
      whileHover: {
        scale: 1.05
      },
      whileTap: {
        scale: .95
      }
    }, "🛒")));
  }))));
}

/* ============ stats ============ */
function Counter({
  to,
  suffix
}) {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  const started = useRef(false);
  React.useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const t0 = performance.now();
        const dur = 1400;
        const tick = t => {
          const p = Math.min(1, (t - t0) / dur);
          setVal(Math.round(to * (1 - Math.pow(1 - p, 3))));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, {
      threshold: .5
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to]);
  return /*#__PURE__*/React.createElement("h3", {
    ref: ref
  }, val.toLocaleString(), suffix);
}
function Stats() {
  const data = [{
    to: 60,
    suffix: 'B',
    label: 'CFU in our max‑strength probiotic',
    color: 'var(--navy-bright)'
  }, {
    to: 17,
    suffix: '',
    label: 'probiotic strains across the line',
    color: 'var(--green-bright)'
  }, {
    to: 19,
    suffix: '',
    label: 'vitamins & minerals in one chewable',
    color: 'var(--orange)'
  }, {
    to: 0,
    suffix: 'g',
    label: 'added sugar. Zero. Always.',
    color: 'var(--accent-yellow-text)'
  }];
  return /*#__PURE__*/React.createElement("section", {
    className: "section",
    style: {
      paddingTop: 30
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "wrap"
  }, /*#__PURE__*/React.createElement(Reveal, {
    className: "stats",
    variants: stagger,
    amount: .4
  }, data.map(d => /*#__PURE__*/React.createElement(motion.div, {
    className: "stat",
    key: d.label,
    variants: fadeUp
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      color: d.color
    }
  }, /*#__PURE__*/React.createElement(Counter, {
    to: d.to,
    suffix: d.suffix
  })), /*#__PURE__*/React.createElement("p", null, d.label))))));
}

/* ============ book promo ============ */
function BookPromo({
  showToast
}) {
  const book = PRODUCTS['book'];
  return /*#__PURE__*/React.createElement("section", {
    className: "section"
  }, /*#__PURE__*/React.createElement("div", {
    className: "wrap"
  }, /*#__PURE__*/React.createElement(Reveal, {
    className: "split",
    amount: .3
  }, /*#__PURE__*/React.createElement(motion.img, {
    src: book.img,
    alt: "Wrong Gummy — Kids Edition book cover",
    whileHover: {
      rotate: -2.5,
      scale: 1.04
    },
    transition: {
      type: 'spring',
      stiffness: 220,
      damping: 16
    }
  }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
    className: "eyebrow orange"
  }, "New · The Book"), /*#__PURE__*/React.createElement("h2", {
    className: "h-md"
  }, "Wrong Gummy ", /*#__PURE__*/React.createElement("span", {
    className: "accent-yellow"
  }, "…Chew On This!!!")), /*#__PURE__*/React.createElement("p", null, "Supplements, explained in kids' language. The doctor's honest dose on what helps, what's hype, and why that cute gummy bear might be plain candy. Written by a pediatric gastroenterologist — read it with your kids before your next trip down the vitamin aisle."), /*#__PURE__*/React.createElement("div", {
    className: "pp-price-row"
  }, /*#__PURE__*/React.createElement("span", {
    className: "price-tag",
    style: {
      fontSize: 30,
      color: 'var(--price-color)'
    }
  }, "$", book.price.toFixed(2)), /*#__PURE__*/React.createElement(motion.a, {
    className: "btn btn-yellow",
    href: book.page,
    whileHover: {
      scale: 1.05
    },
    whileTap: {
      scale: .95
    }
  }, "Peek Inside →"), /*#__PURE__*/React.createElement(motion.button, {
    className: "btn btn-ghost",
    onClick: buyClick(showToast, book),
    whileHover: {
      scale: 1.05
    },
    whileTap: {
      scale: .95
    }
  }, "Add to Cart"))))));
}

/* ============ blog teaser ============ */
function JournalTeaser() {
  const cats = window.BLOG_CATEGORIES,
    posts = (window.BLOG || []).slice(0, 3);
  if (!posts.length) return null;
  return /*#__PURE__*/React.createElement("section", {
    className: "section",
    style: {
      paddingTop: 20
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "wrap"
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 16,
      marginBottom: 40
    }
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Reveal, null, /*#__PURE__*/React.createElement("span", {
    className: "eyebrow green"
  }, "The Zumm Journal")), /*#__PURE__*/React.createElement(Reveal, null, /*#__PURE__*/React.createElement("h2", {
    className: "h-lg",
    style: {
      marginTop: 16
    }
  }, "Gut health, ", /*#__PURE__*/React.createElement("span", {
    className: "accent-green"
  }, "made simple")))), /*#__PURE__*/React.createElement(Reveal, null, /*#__PURE__*/React.createElement(motion.a, {
    className: "btn btn-ghost",
    href: "blog.html",
    whileHover: {
      scale: 1.05
    },
    whileTap: {
      scale: .95
    }
  }, "Read the blog →"))), /*#__PURE__*/React.createElement(Reveal, {
    className: "blog-grid",
    variants: stagger,
    amount: .15
  }, posts.map(p => {
    const c = cats[p.category];
    return /*#__PURE__*/React.createElement(motion.a, {
      key: p.slug,
      href: `blog-${p.slug}.html`,
      className: "bcard",
      variants: fadeUp,
      whileHover: {
        y: -8,
        borderColor: c.color
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "bcard-top",
      style: {
        '--bc-glow': c.chip
      }
    }, p.heroImg ? /*#__PURE__*/React.createElement("img", {
      className: "bcard-cover",
      src: p.heroImg,
      alt: p.title,
      loading: "lazy"
    }) : /*#__PURE__*/React.createElement("span", null, p.heroEmoji), /*#__PURE__*/React.createElement("span", {
      className: "bcard-cat",
      style: {
        background: c.chip,
        color: c.color,
        border: `1px solid ${c.bd}`
      }
    }, p.category)), /*#__PURE__*/React.createElement("div", {
      className: "bcard-body"
    }, /*#__PURE__*/React.createElement("h3", null, p.title), /*#__PURE__*/React.createElement("p", null, p.excerpt), /*#__PURE__*/React.createElement("span", {
      className: "bcard-arrow"
    }, "Read article →")));
  }))));
}

/* ============ page ============ */
function HomePage() {
  const [toastNode, showToast] = useToast();
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TopBar, null), /*#__PURE__*/React.createElement(Nav, {
    current: "index.html"
  }), /*#__PURE__*/React.createElement(Hero, null), /*#__PURE__*/React.createElement(OrbitSection, {
    showToast: showToast
  }), /*#__PURE__*/React.createElement(ProductGrid, {
    showToast: showToast
  }), /*#__PURE__*/React.createElement(Marquee, {
    items: ['No Gummies', 'No Sugar', 'No Nonsense', 'Pediatrician‑Formulated', 'Third‑Party Tested', 'GMP‑Certified']
  }), /*#__PURE__*/React.createElement(Why, null), /*#__PURE__*/React.createElement(Stats, null), /*#__PURE__*/React.createElement(BookPromo, {
    showToast: showToast
  }), /*#__PURE__*/React.createElement(JournalTeaser, null), /*#__PURE__*/React.createElement(Footer, null), toastNode);
}
ReactDOM.createRoot(document.getElementById('root')).render(/*#__PURE__*/React.createElement(HomePage, null));
})();
